/**
 * Unified Health Service
 *
 * Platform-agnostic health data service using @capgo/capacitor-health.
 * All health data collection logic is driven by configuration from health-data-config.ts.
 */

import { Health, HealthDataType } from "@capgo/capacitor-health";
import { v5 as uuidv5 } from "uuid";
import {
  getEnabledDataTypes,
  getDataTypeConfig,
  getReadPermissions,
  DataTypeConfig,
} from "../config/health-data-config";
import { normaliseUploadValue } from "./spo2Scale";
import crashlytics from "./crashlytics";
import analytics from "./firebaseAnalytics";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface HealthDataResult {
  [dataType: string]: {
    countReturn: number;
    resultData: any[];
  } | null;
}

export interface PermissionResult {
  granted: boolean;
  dataTypes?: HealthDataType[];
  error?: string;
}

export interface ServerEvent {
  time_zone: string;
  activity_type: string;
  start_date: string;
  end_date: string;
  user_local_date: string; // yyyy-mm-dd format in user's local timezone
  uuid: string;
  source: string;
  source_bundle_id: string;
  numeric_value?: number;
  state_value?: string;
  duration?: number;
  activity_device_id?: string;
  metadata?: Record<string, any>;
}

// ============================================================================
// RATE LIMITING
// ============================================================================
// Note: Rate limiting is handled at the entry point (healthkit.ts)
// This service is called internally and does not perform its own rate limiting

// ============================================================================
// UUID GENERATION
// ============================================================================

/**
 * UUID namespace for health data (using DNS namespace from uuidv5)
 */
const NAMESPACE = uuidv5.DNS;

/**
 * Generate deterministic UUID for health records
 *
 * UUID generation strategy ensures unique identification across:
 * - Different users (profile_id)
 * - Different data sources (sourceBundleId/sourceName)
 * - Different data types (dataType) ← CRITICAL: Prevents steps/distance/calories collision!
 * - Different time periods
 * - Different values (for point readings)
 *
 * UUID FORMULA BY DATA TYPE:
 *
 * 1. AGGREGATED DATA (steps, distance, calories):
 *    UUID = profile_id + dataType + source + startDate + endDate
 *    - Same day + same source + same type = same UUID (value can update)
 *    - Different types (steps vs calories) on same day = DIFFERENT UUIDs ✓
 *
 * 2. SLEEP SESSIONS:
 *    UUID = profile_id + dataType + source + startDate + endDate
 *    - Same sleep window = same UUID (sleep stage/state can update)
 *    - dataType included for consistency and future-proofing
 *
 * 3. WORKOUTS:
 *    UUID = profile_id + dataType + source + startDate + endDate + workoutType
 *    - Duration/distance/calories can be corrected without changing UUID
 *    - dataType included for consistency
 *
 * 4. POINT-IN-TIME READINGS (heart rate, oxygen, respiratory rate, etc.):
 *    UUID = profile_id + dataType + source + startDate + value
 *    - Each measurement is unique by timestamp and value
 *    - dataType CRITICAL: heart rate 72 ≠ oxygen saturation 72 at same time!
 *
 * @param record - The health data record
 * @param platform - 'ios' or 'android'
 * @param dataType - Type of health data (REQUIRED for uniqueness)
 * @param fetchStrategy - 'aggregated' or 'raw' (from config)
 * @param profileId - User's profile ID to prevent cross-user data collisions
 * @returns Deterministic UUID string
 */
function generateUUID(
  record: any,
  platform: string,
  dataType: any,
  fetchStrategy: 'aggregated' | 'raw',
  profileId: number
): string {
  // Extract source identifier
  const sourceStr =
    record.sourceBundleId ||
    record.sourceId ||
    record.sourceName ||
    platform;

  // Extract timestamps
  const startDateStr = record.startDate || "";
  const endDateStr = record.endDate || record.startDate || "";

  // Extract value (for raw samples)
  const valueStr = record.value !== undefined ? record.value.toString() : "";

  // Build unique name based on data type and fetch strategy
  // CRITICAL: dataType is ALWAYS included to prevent collision between different metric types!
  let name: string;

  if (fetchStrategy === 'aggregated') {
    // AGGREGATED DATA: Daily totals (steps, distance, calories)
    // MUST include dataType to differentiate steps from distance from calories!
    // Without dataType: all three would have the same UUID for the same day/source
    name = `${profileId}|${dataType}|${sourceStr}|${startDateStr}|${endDateStr}`;

  } else if (dataType === 'sleep') {
    // SLEEP: Identified by time window + dataType
    // Same sleep session = same UUID (sleep state can update)
    // Include dataType for consistency and future-proofing
    name = `${profileId}|${dataType}|${sourceStr}|${startDateStr}|${endDateStr}`;

  } else if (dataType === 'workouts') {
    // WORKOUTS: Identified by time window + workout type + dataType
    // Duration/distance/calories can change (corrections), but it's the same workout
    // Include dataType for consistency
    const workoutType = record.workoutType || 'unknown';
    name = `${profileId}|${dataType}|${sourceStr}|${startDateStr}|${endDateStr}|${workoutType}`;

  } else {
    // RAW POINT READINGS: heart rate, oxygen saturation, respiratory rate, etc.
    // MUST include dataType: heart rate 72 ≠ oxygen saturation 72 at same timestamp!
    // Without dataType: different metrics with same value at same time would collide
    name = `${profileId}|${dataType}|${sourceStr}|${startDateStr}|${valueStr}`;
  }

  // Generate UUID v5 (deterministic hash)
  // Same inputs will ALWAYS produce the same UUID
  return uuidv5(name, NAMESPACE);
}

// ============================================================================
// UNIFIED HEALTH SERVICE
// ============================================================================

export class UnifiedHealthService {
  /**
   * Check if health services are available on the device
   */
  static async isAvailable(): Promise<boolean> {
    try {
      const result = await Health.isAvailable();
      return result.available === true;
    } catch (error) {
      console.error("Health service not available:", error);
      return false;
    }
  }

  /**
   * Request health data permissions based on platform cxonfiguration
   */
  static async requestPermissions(
    platform: "ios" | "android"
  ): Promise<PermissionResult> {
    try {
      const readPermissions = getReadPermissions(platform);

      console.log("=".repeat(80));
      console.log("🔐 REQUESTING HEALTH PERMISSIONS");
      console.log("=".repeat(80));
      console.log(`Platform: ${platform}`);
      console.log(`Permissions requested: ${readPermissions.length}`);
      console.log(`Data types: ${readPermissions.join(", ")}`);

      // Request authorization (shows permission dialog)
      console.log("\n📱 Showing permission dialog to user...");
      await Health.requestAuthorization({
        read: readPermissions,
        write: [], // No write permissions for now
      });

      // IMPORTANT: requestAuthorization doesn't reliably return what was granted
      // (especially on iOS due to privacy protections)
      // We must call checkAuthorization separately to verify actual permissions
      console.log("\n🔍 Verifying actual authorization status...");

      const authStatus = await Health.checkAuthorization({
        read: readPermissions,
      });

      console.log("\n📊 Authorization Result:");
      console.log(`Authorized: ${authStatus.readAuthorized.length}`);
      console.log(`Denied: ${authStatus.readDenied.length}`);

      // Check if ANY permissions were granted (not requiring all)
      // We can still fetch data for the permissions that were granted
      const someGranted = authStatus.readAuthorized.length > 0;

      // Log what was granted and denied
      if (authStatus.readAuthorized.length > 0) {
        console.log(
          `\n✅ AUTHORIZED (${authStatus.readAuthorized.length}/${
            readPermissions.length
          }):`
        );
        authStatus.readAuthorized.forEach((type) => console.log(`  - ${type}`));
      }

      if (authStatus.readDenied.length > 0) {
        console.warn(
          `\n❌ DENIED (${authStatus.readDenied.length}/${
            readPermissions.length
          }):`
        );
        authStatus.readDenied.forEach((type) => console.warn(`  - ${type}`));
      }

      console.log("=".repeat(80));
      console.log("\n");

      return {
        granted: someGranted, // TRUE if at least one permission granted
        dataTypes: [...authStatus.readAuthorized, "workouts"] as any,
      }; // Include "workouts" as it's not reported from checkAuthorization correctly
    } catch (error: any) {
      console.error("\n❌ ERROR requesting health permissions:", error);
      console.error("Error details:", {
        message: error.message,
        code: error.code,
        stack: error.stack,
      });
      return {
        granted: false,
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Check if permissions have been granted for health data
   */
  static async checkAuthorization(
    platform: "ios" | "android"
  ): Promise<boolean> {
    try {
      const readPermissions = getReadPermissions(platform);

      const result = await Health.checkAuthorization({
        read: readPermissions,
      });

      // Check if all requested permissions are authorized
      const allAuthorized = readPermissions.every((perm) =>
        result.readAuthorized.includes(perm)
      );

      return allAuthorized;
    } catch (error) {
      console.error("Error checking authorization:", error);
      return false;
    }
  }

  /**
   * Fetch health data for all enabled data types
   * Note: Rate limiting is handled at the entry point (healthkit.ts)
   */
  static async fetchHealthData(
    platform: "ios" | "android",
    startDate: Date,
    endDate: Date
  ): Promise<HealthDataResult> {
    console.log("=".repeat(80));
    console.log("📊 FETCHING HEALTH DATA");
    console.log("=".repeat(80));

    // Validate date range
    if (!startDate || !endDate) {
      throw new Error("Invalid date range: start and end dates are required");
    }

    if (startDate >= endDate) {
      throw new Error("Invalid date range: start date must be before end date");
    }

    console.log(`Platform: ${platform}`);
    console.log(`Date Range: ${startDate.toISOString()} → ${endDate.toISOString()}`);
    console.log(`Timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`);

    // Get enabled data types from platform configuration
    const enabledTypes = getEnabledDataTypes(platform);

    console.log(`\n📋 Enabled Data Types: ${enabledTypes.length}`);

    // Fetch all enabled data types in parallel for better performance
    const results: HealthDataResult = {};

    console.log("\n🔄 Starting parallel data fetch...\n");

    // Create fetch promises for all data types
    const fetchPromises = enabledTypes.map(async (dataType) => {
      const typeConfig = getDataTypeConfig(platform, dataType);
      if (!typeConfig) {
        console.warn(`⚠️ No config for ${dataType}`);
        return { dataType, result: null };
      }

      try {
        const result = await this.fetchDataType(
          dataType,
          typeConfig,
          startDate,
          endDate
        );

        return { dataType, result };
      } catch (error: any) {
        console.error(`❌ ${dataType} ERROR:`, error.message || error);
        return { dataType, result: null };
      }
    });

    // Wait for all fetches to complete in parallel
    const fetchResults = await Promise.all(fetchPromises);

    // Populate results object
    fetchResults.forEach(({ dataType, result }) => {
      results[dataType] = result;
    });

    // Summary
    const totalRecords = Object.values(results).reduce(
      (sum, r) => sum + (r?.countReturn || 0),
      0
    );
    const successCount = Object.values(results).filter((r) => r !== null).length;

    console.log("\n" + "=".repeat(80));
    console.log(`📈 FETCH COMPLETE`);
    console.log(`Success: ${successCount}/${enabledTypes.length} data types`);
    console.log(`Total Records: ${totalRecords}`);
    console.log("=".repeat(80));
    console.log("\n");

    return results;
  }

  /**
   * Fetch a single data type based on its configuration
   */
  private static async fetchDataType(
    dataType: any,
    config: DataTypeConfig,
    startDate: Date,
    endDate: Date
  ): Promise<{ countReturn: number; resultData: any[] }> {
    const results: any[] = [];

    // Get timezone-aware date strings
    const startDateISO = startDate.toISOString();
    const endDateISO = endDate.toISOString();

    // Special case: workouts use a different API method
    if (dataType === "workouts") {
      try {
        const workouts = await Health.queryWorkouts({
          startDate: startDateISO,
          endDate: endDateISO,
          limit: config.limit || 10000,
        });

        if (workouts.workouts && workouts.workouts.length > 0) {
          try {
            // Convert workout format with validation
            const formattedWorkouts = workouts.workouts
              .map((workout: any, index: number) => {
                try {
                  // Validate required fields
                  if (!workout.startDate || !workout.endDate || !workout.workoutType) {
                    console.warn(`Skipping workout ${index}: missing required fields`, {
                      hasStartDate: !!workout.startDate,
                      hasEndDate: !!workout.endDate,
                      hasWorkoutType: !!workout.workoutType
                    });

                    // Log to analytics for monitoring
                    analytics.logEvent("health_workout_missing_fields", {
                      has_start_date: !!workout.startDate,
                      has_end_date: !!workout.endDate,
                      has_workout_type: !!workout.workoutType,
                    });

                    return null;
                  }

                  return {
                    startDate: workout.startDate,
                    endDate: workout.endDate,
                    value: workout.duration || 0,
                    workoutType: workout.workoutType,
                    totalEnergyBurned: workout.totalEnergyBurned || 0,
                    totalDistance: workout.totalDistance || 0,
                    sourceName: workout.sourceName || "Unknown",
                    sourceId: workout.sourceId || "",
                    metadata: workout.metadata || {},
                  };
                } catch (error: any) {
                  console.error(`Error formatting workout ${index}:`, error.message);

                  // Log error to crashlytics
                  crashlytics.recordException(error, {
                    context: "workout_format_conversion",
                    workout_index: index,
                  });

                  return null;
                }
              })
              .filter((workout): workout is NonNullable<typeof workout> => workout !== null);

            if (formattedWorkouts.length > 0) {
              results.push(...formattedWorkouts);

              // Log success metrics
              const skippedCount = workouts.workouts.length - formattedWorkouts.length;
              if (skippedCount > 0) {
                console.warn(`⚠️ Skipped ${skippedCount}/${workouts.workouts.length} invalid workouts`);
                analytics.logEvent("health_workouts_skipped", {
                  total_workouts: workouts.workouts.length,
                  valid_workouts: formattedWorkouts.length,
                  skipped_count: skippedCount,
                });
              }
            } else {
              console.warn(`⚠️ No valid workouts after formatting`);
              analytics.logEvent("health_workouts_all_invalid", {
                total_workouts: workouts.workouts.length,
              });
            }
          } catch (error: any) {
            console.error(`❌ Error converting workouts:`, error.message);

            // Log critical error to crashlytics
            crashlytics.recordException(error, {
              context: "workout_batch_conversion",
              workout_count: workouts.workouts.length,
            });
          }
        }
      } catch (error: any) {
        console.error(`❌ Error fetching workouts:`, error.message || error);
      }

      return {
        countReturn: results.length,
        resultData: results,
      };
    }

    // Fetch based on strategy (aggregated or raw, "both" option removed)
    if (config.fetchStrategy === "aggregated") {
      try {
        const aggregated = await Health.queryAggregated({
          dataType,
          startDate: startDateISO,
          endDate: endDateISO,
          bucket: config.aggregationBucket || "day",
          aggregation: config.aggregationMethod || "sum",
        });

        if (aggregated.samples && aggregated.samples.length > 0) {
          results.push(...aggregated.samples);
        }
      } catch (error: any) {
        console.error(`❌ Error fetching aggregated ${dataType}:`, error.message || error);
      }
    } else if (config.fetchStrategy === "raw") {
      try {
        const samples = await Health.readSamples({
          dataType,
          startDate: startDateISO,
          endDate: endDateISO,
          limit: config.limit || 100000,
        });

        if (samples.samples && samples.samples.length > 0) {
          results.push(...samples.samples);
        }
      } catch (error: any) {
        console.error(`❌ Error fetching raw ${dataType}:`, error.message || error);
      }
    }

    return {
      countReturn: results.length,
      resultData: results,
    };
  }

  /**
   * Convert health data to server format
   * @param platform - 'ios' or 'android'
   * @param healthData - Health data fetched from the platform
   * @param profileId - User's profile ID to prevent cross-user data collisions
   */
  static convertToServerFormat(
    platform: "ios" | "android",
    healthData: HealthDataResult,
    profileId: number
  ): ServerEvent[] {
    console.log("=".repeat(80));
    console.log("🔄 CONVERTING TO SERVER FORMAT");
    console.log("=".repeat(80));
    console.log(`Platform: ${platform}`);
    console.log(`Timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`);

    const events: ServerEvent[] = [];
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const dataTypesWithData = Object.entries(healthData).filter(
      ([_, data]) => data && data.resultData && data.resultData.length > 0
    );

    console.log(`\n📊 Data types with data: ${dataTypesWithData.length}`);

    Object.entries(healthData).forEach(([dataType, data]) => {
      if (!data || !data.resultData || data.resultData.length === 0) {
        return;
      }

      const typeConfig = getDataTypeConfig(
        platform,
        dataType as any
      );
      if (!typeConfig) {
        console.warn(`⚠️ No config found for data type: ${dataType}`);
        return;
      }

      const activityType = typeConfig.serverActivityType;
      data.resultData.forEach((record: any, index: number) => {
        try {
          // Generate UUID with fetch strategy from config
          const uuid = generateUUID(
            record,
            platform,
            dataType as any,
            typeConfig.fetchStrategy,
            profileId
          );

          // Extract user's local date (yyyy-mm-dd) from start_date in user's timezone
          // Use start_date to avoid off-by-one errors when end_date is at midnight
          const startDate = new Date(record.startDate);
          const year = startDate.getFullYear();
          const month = String(startDate.getMonth() + 1).padStart(2, '0');
          const day = String(startDate.getDate()).padStart(2, '0');
          const userLocalDate = `${year}-${month}-${day}`;

          // Build base event
          const event: ServerEvent = {
            time_zone: timezone,
            activity_type: activityType,
            start_date: record.startDate,
            end_date: record.endDate || record.startDate,
            user_local_date: userLocalDate,
            uuid: uuid,
            source:
              record.sourceName ||
              record.sourceId ||
              (platform === "ios" ? "HealthKit" : "Health Connect"),
            source_bundle_id:
              record.sourceBundleId ||
              (platform === "ios"
                ? "com.apple.health"
                : "com.google.android.apps.healthdata"),
          };

          // Add numeric value if present
          if (record.value !== undefined) {
            // F-827: SpO2 always leaves the app as a fraction (Health Connect sends a percent).
            event.numeric_value =
              typeof record.value === "number"
                ? normaliseUploadValue(activityType, record.value)
                : record.value;
          }

          // Sleep data prioritizes sleepState, but preserves state if different
          if (record.sleepState !== undefined) {
            event.state_value = record.sleepState;
            // If state exists and differs, preserve in metadata
            if (record.state !== undefined && record.state !== record.sleepState) {
              if (!event.metadata) event.metadata = {};
              event.metadata.originalState = record.state;
            }
          } else if (record.state !== undefined) {
            // Fallback to state if sleepState not available
            event.state_value = record.state;
          }

          // Calculate duration in hours if both dates present
          if (record.startDate && record.endDate) {
            const start = new Date(record.startDate);
            const end = new Date(record.endDate);

            // Validate dates are valid
            if (isNaN(start.getTime()) || isNaN(end.getTime())) {
              console.warn(`Invalid date in health record - skipping duration:`, {
                startDate: record.startDate,
                endDate: record.endDate,
                dataType: dataType
              });

              // Log to analytics for monitoring
              analytics.logEvent("health_data_invalid_date", {
                data_type: dataType,
                has_start: !!record.startDate,
                has_end: !!record.endDate,
              });
            } else {
              const durationMs = end.getTime() - start.getTime();

              // Validate duration is positive and reasonable
              if (durationMs < 0) {
                console.warn(`Negative duration in health record - using absolute value:`, {
                  startDate: record.startDate,
                  endDate: record.endDate,
                  durationHours: durationMs / (1000 * 60 * 60)
                });

                // Log to analytics for monitoring
                analytics.logEvent("health_data_negative_duration", {
                  data_type: dataType,
                  duration_hours: durationMs / (1000 * 60 * 60),
                });

                event.duration = Math.abs(durationMs) / (1000 * 60 * 60);
              } else if (durationMs > 48 * 60 * 60 * 1000) {
                // Duration > 48 hours is suspicious but may be valid (e.g., long sleep session)
                console.warn(`Long duration detected (>48h):`, {
                  durationHours: durationMs / (1000 * 60 * 60),
                  dataType: dataType
                });
                event.duration = durationMs / (1000 * 60 * 60);
              } else {
                event.duration = durationMs / (1000 * 60 * 60);
              }
            }
          }

          // Add metadata if available
          const metadata: Record<string, any> = {};

          if (record.unit) {
            metadata.unitName = record.unit;
          }

          // Add workout-specific metadata
          if (record.workoutType) {
            metadata.workoutType = record.workoutType;
          }
          if (record.totalEnergyBurned !== undefined) {
            metadata.totalEnergyBurned = record.totalEnergyBurned;
          }
          if (record.totalDistance !== undefined) {
            metadata.totalDistance = record.totalDistance;
          }

          if (record.metadata) {
            Object.assign(metadata, record.metadata);
          }

          if (Object.keys(metadata).length > 0) {
            event.metadata = metadata;
          }

          events.push(event);
        } catch (error: any) {
          console.error(
            `  ❌ Error converting ${dataType} record ${index}:`,
            error.message || error
          );
        }
      });
    });

    console.log("\n" + "=".repeat(80));
    console.log(`✅ CONVERSION COMPLETE`);
    console.log(`Total events: ${events.length}`);
    console.log("=".repeat(80));
    console.log("\n");

    return events;
  }
}
